import { toast } from "sonner";
import { adminAuthService } from "@/services/adminAuthService";

interface ApiClientOptions extends RequestInit {
  headers?: HeadersInit;
  /** 是否禁用自动 toast 提示（默认 false，即默认显示） */
  disableAutoToast?: boolean;
  /** 是否返回完整响应（默认 false，即只返回 data 字段） */
  returnFullResponse?: boolean;
}

/**
 * API 响应格式（统一格式）
 */
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  meta?: Record<string, any>;
  message?: string;
  error?: {
    code: string;
    message: string;
    details?: Record<string, any>;
  };
}

/**
 * 统一的 API 请求工具，自动注入 CSRF Token 和 JWT Token，使用原生 fetch。
 *
 * 功能：
 * 1. 自动注入 CSRF Token 和 JWT Token
 * 2. 自动处理响应中的 message 字段，通过 toast 显示
 * 3. 自动处理错误响应，显示错误提示
 *
 * @param url 请求路径，如 "/api/v1/point_histories"
 * @param options 其他 fetch 选项
 * @returns 解析后的 JSON 数据
 *
 * @example
 * // 成功响应会自动显示 toast
 * const data = await apiClient('/api/v1/users/1');
 *
 * @example
 * // 禁用自动 toast
 * const data = await apiClient('/api/v1/users/1', { disableAutoToast: true });
 */
async function apiClient<T>(
  url: string,
  options: ApiClientOptions = {}
): Promise<T> {
  const {
    disableAutoToast = false,
    returnFullResponse = false,
    ...fetchOptions
  } = options;

  const csrfToken = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]'
  )?.content;

  const token = adminAuthService.getAccessToken();

  // 获取当前语言设置
  const currentLanguage = localStorage.getItem("i18nextLng") || "zh";

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "Accept-Language": currentLanguage,
    ...fetchOptions.headers,
  };

  if (csrfToken) {
    (headers as Record<string, string>)["X-CSRF-Token"] = csrfToken;
  }

  if (token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  // 处理错误响应
  if (!response.ok) {
    let errorMessage = `请求失败：${response.status}`;

    try {
      const errorData: ApiResponse = await response.json();

      // 提取错误消息
      if (errorData.error?.message) {
        errorMessage = errorData.error.message;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }

      // 显示错误提示
      if (!disableAutoToast) {
        toast.error(errorMessage);
      }

      throw new Error(errorMessage);
    } catch (parseError) {
      // JSON 解析失败（可能是 HTML 错误页面）
      // 使用默认错误消息
      if (!disableAutoToast) {
        toast.error(errorMessage);
      }
      throw new Error(errorMessage);
    }
  }

  // 处理 204 No Content
  if (response.status === 204) {
    // @ts-expect-error 返回 void
    return undefined;
  }

  // 解析成功响应
  const responseData: ApiResponse<T> = await response.json();

  // 自动显示成功消息（如果有）
  if (!disableAutoToast && responseData.message) {
    toast.success(responseData.message);
  }

  if (returnFullResponse) {
    return responseData as unknown as T;
  }

  // 返回数据部分（兼容旧格式和新格式）
  if (responseData.data !== undefined) {
    return responseData.data as T;
  }

  // 如果没有 data 字段，返回整个响应（向后兼容）
  return responseData as T;
}

export default apiClient;
